/**
 * <s-newsletter-popup> — delayed newsletter signup dialog
 *
 * Opens its native <dialog> modally after `delay` seconds, at most once per
 * visitor until `days-hidden` days have passed since it was closed. A visitor
 * who subscribes through the popup is not shown it again for a year.
 * After the signup form posts back, the popup reopens straight away so the
 * visitor sees the success or error message.
 *
 * Attributes:
 *   delay        — seconds before opening (default 20)
 *   days-hidden  — days to wait after a close before showing again (default 30)
 *
 * Add `?preview_popup=1` to any storefront URL to open it immediately.
 */
const STORAGE_KEY = 'fdt-newsletter-popup-hidden-until';
const SUBMIT_KEY = 'fdt-newsletter-popup-submitted';
const DAY_MS = 24 * 60 * 60 * 1000;

class SNewsletterPopup extends HTMLElement {
  #dialog;
  #ac;
  #timer = null;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#dialog.addEventListener('click', (e) => {
      if (e.target === this.#dialog) this.#dialog.close();
    }, opts);

    this.#dialog.addEventListener('close', () => {
      if (!this.#isHidden()) this.#hideFor(this.#daysHidden);
    }, opts);

    this.#dialog.addEventListener('submit', () => {
      this.#store(SUBMIT_KEY, '1', sessionStorage);
    }, opts);

    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:block:select', (e) => {
        if (this.contains(e.target) && !this.#dialog.open) this.#dialog.showModal();
      }, opts);
      document.addEventListener('shopify:block:deselect', (e) => {
        if (this.contains(e.target) && this.#dialog.open) this.#dialog.close();
      }, opts);
      return;
    }

    const params = new URLSearchParams(window.location.search);

    if (this.#read(SUBMIT_KEY, sessionStorage)) {
      this.#remove(SUBMIT_KEY, sessionStorage);
      if (params.get('customer_posted') === 'true') this.#hideFor(365);
      this.#open();
      return;
    }

    if (params.get('preview_popup') === '1') {
      this.#open();
      return;
    }

    if (this.#isHidden()) return;

    this.#schedule(this.#delay * 1000);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#timer);
  }

  get #delay() {
    const value = Number(this.getAttribute('delay'));
    return Number.isFinite(value) && value >= 0 ? value : 20;
  }

  get #daysHidden() {
    const value = Number(this.getAttribute('days-hidden'));
    return Number.isFinite(value) && value > 0 ? value : 30;
  }

  #schedule(ms) {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => {
      // Never stack on top of the cart, menu or search panels: try again shortly.
      if (document.querySelector('dialog[open]')) {
        this.#schedule(5000);
        return;
      }
      this.#open();
    }, ms);
  }

  #open() {
    if (!this.#dialog.open) this.#dialog.showModal();
  }

  #isHidden() {
    const until = Number(this.#read(STORAGE_KEY, localStorage));
    return Number.isFinite(until) && until > Date.now();
  }

  #hideFor(days) {
    this.#store(STORAGE_KEY, String(Date.now() + days * DAY_MS), localStorage);
  }

  #read(key, storage) {
    try { return storage.getItem(key); } catch { return null; }
  }

  #store(key, value, storage) {
    try { storage.setItem(key, value); } catch { /* storage unavailable */ }
  }

  #remove(key, storage) {
    try { storage.removeItem(key); } catch { /* storage unavailable */ }
  }
}

if (!customElements.get('s-newsletter-popup')) {
  customElements.define('s-newsletter-popup', SNewsletterPopup);
}
